/**
 * Shared types for the BriefCreatorOverlay state machine.
 */

import type { BriefDraft, AdPreview, BriefTemplate } from '@/services/api';

export type OverlayIntent =
  | 'suggested'
  | 'freeform'
  | 'materials'
  | 'from_ad'
  | 'template';

export type OverlayStep =
  | 'client-persona'
  | 'intent'
  | 'input'
  | 'preview'
  | 'committing';

/**
 * The local draft state that drives the right-side preview pane.
 *
 * Built up incrementally as the user moves through Step 2 of the chosen
 * intent path:
 *   - Suggested Ideas: idea selection populates `seedIdea` + `name`
 *   - Freeform: synthesize result populates `synthesizedBrief`
 *   - Source Materials: synthesize-with-attachments → `synthesizedBrief`
 *   - From an Ad: from-ad result populates `adDraft` + `adPreview`
 *   - Template: chosen template populates `template`
 *
 * The commit step reads exactly one of these (whichever is populated for
 * the active intent) and constructs a `BriefCreatorCommitPayload`.
 */
export interface DraftState {
  intent: OverlayIntent | null;

  /** Ranked-idea path */
  seedIdea?: {
    idea_id: string;
    title: string;
    premise: string;
    scenario_ids: string[];
    persona_id?: string;
  };

  /** Freeform / source-materials path — already-persisted brief returned by synthesize */
  synthesizedBrief?: {
    brief_id: string;
    name: string;
    sections: Record<string, Record<string, unknown>>;
    persona_id?: string;
  };

  /** From-an-Ad path */
  adDraft?: BriefDraft;
  adPreview?: AdPreview;

  /** Template path */
  template?: BriefTemplate;

  /** Current draft name shown in the preview header */
  name: string;

  /** Auxiliary attachment IDs (source materials path) */
  attachmentIds?: string[];

  /** Free-text seed (freeform path / suggested premise) */
  freeformText?: string;
}

export const EMPTY_DRAFT_STATE: DraftState = {
  intent: null,
  name: '',
};
